import React,{useEffect,useMemo,useState} from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import Flag from 'react-world-flags'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import './settings.css'

/* ------------------------------------------------------------------
   Icons — stroke: currentColor so they inherit the token colour.
   ------------------------------------------------------------------ */
const IconEdit=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 20h9"/>
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
  </svg>
)

const IconX=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>
)

const IconSearch=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7"/>
    <path d="m20 20-3.5-3.5"/>
  </svg>
)

/* ------------------------------------------------------------------
   THEME TOKENS
   react-data-table-component styles through a JS object, so the table
   cannot read CSS variables on its own. We read them off :root and
   re-read whenever the theme attribute or the OS preference changes.
   (This is what replaced the hard-coded #55505c / #fff customStyles.)
   ------------------------------------------------------------------ */
const TOKEN_NAMES=[
  '--panel','--panel-raise','--line','--line-soft',
  '--ink','--ink-2','--ink-3','--accent','--accent-wash',
  '--mono','--sans','--step-0'
]

const readTokens=()=>{
  const out={}
  if (typeof window==='undefined'||!document.documentElement) return out
  const styles=window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach(name=>{
    out[name]=String(styles.getPropertyValue(name)||'').trim()
  })
  return out
}

function useThemeTokens(){
  const [tokens,setTokens]=useState(readTokens)

  useEffect(()=>{
    let frame=null
    const refresh=()=>{
      if (frame) window.cancelAnimationFrame(frame)
      frame=window.requestAnimationFrame(()=>setTokens(readTokens()))
    }

    const observer=new MutationObserver(refresh)
    observer.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme','class','style']})

    let media=null
    if (window.matchMedia){
      media=window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change',refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return ()=>{
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media){
        if (media.removeEventListener) media.removeEventListener('change',refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  },[])

  return tokens
}

function buildTableStyles(t){
  return {
    table:{ style:{ backgroundColor:t['--panel'], color:t['--ink'] } },
    tableWrapper:{ style:{ display:'block' } },
    head:{ style:{ color:t['--ink-3'] } },
    headRow:{
      style:{
        backgroundColor:t['--panel-raise'],
        color:t['--ink-3'],
        minHeight:'42px',
        borderBottomWidth:'1px',
        borderBottomColor:t['--line'],
        borderBottomStyle:'solid'
      }
    },
    headCells:{
      style:{
        paddingLeft:'12px',
        paddingRight:'12px',
        fontFamily:t['--mono'],
        fontSize:'10px',
        fontWeight:600,
        letterSpacing:'0.08em',
        textTransform:'uppercase',
        color:t['--ink-3']
      }
    },
    rows:{
      style:{
        backgroundColor:t['--panel'],
        color:t['--ink'],
        fontFamily:t['--sans'],
        fontSize:t['--step-0']||'0.875rem',
        minHeight:'46px',
        '&:not(:last-of-type)':{
          borderBottomWidth:'1px',
          borderBottomStyle:'solid',
          borderBottomColor:t['--line-soft']
        }
      },
      highlightOnHoverStyle:{
        backgroundColor:t['--accent-wash'],
        color:t['--ink'],
        borderBottomColor:t['--line-soft'],
        outlineWidth:'0px',
        outlineColor:t['--panel'],
        transitionDuration:'0.15s',
        transitionProperty:'background-color'
      }
    },
    cells:{ style:{ paddingLeft:'12px', paddingRight:'12px' } },
    pagination:{
      style:{
        backgroundColor:t['--panel'],
        color:t['--ink-3'],
        fontSize:'12px',
        minHeight:'50px',
        borderTopWidth:'1px',
        borderTopStyle:'solid',
        borderTopColor:t['--line']
      },
      pageButtonsStyle:{
        color:t['--ink-2'],
        fill:t['--ink-2'],
        backgroundColor:'transparent',
        '&:disabled':{ color:t['--ink-3'], fill:t['--ink-3'] },
        '&:hover:not(:disabled)':{ backgroundColor:t['--panel-raise'] },
        '&:focus':{ outline:'none', backgroundColor:t['--accent-wash'] }
      }
    },
    noData:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } },
    progress:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } }
  }
}

const YesNo=({on})=>(
  <span className="st-pill" data-on={on ? '1' : '0'}>{on ? 'YES' : 'NO'}</span>
)

export default function Countries() {

    const [data,setData]=useState([])
    const [show,setShow]=useState(false)
    const [modalTitle,setModalTitle]=useState('')
    const [record,setRecord]=useState({})
    /* true from the first paint: loadData() fires on mount */
    const [isLoading,setIsLoading]=useState(true)
    const [failed,setFailed]=useState(false)
    const [lang,setLang]=useState([])
    const [q,setQ]=useState('')

    /* inline feedback — this is what replaced the alert() calls */
    const [note,setNote]=useState(null)
    const [modalNote,setModalNote]=useState(null)
    const [askDelete,setAskDelete]=useState(false)

    const tokens=useThemeTokens()

    const columns = [
        {
            name: '',
            cell: row => (
              <button
                type="button"
                className="st-rowbtn"
                onClick={()=>showForm(row.CountryID)}
                aria-label={'Edit '+(row.Country||'')}
                title="Edit"
              >
                <IconEdit/>
              </button>
            ),
            width: '62px',
            center: true,
            ignoreRowClick: true
        },
        {
            name: '',
            cell: row => <Flag className="st-flag" code={ row.CountryCode } height="22" width="22"/>,
            width: '54px',
            center: true
        },
        {
            name: 'COUNTRY NAME',
            selector: row => row.Country,
            cell: row => <span className="st-cell-name">{row.Country}</span>,
            sortable: true,
            minWidth: '180px'
        },
        {
            name: 'EU',
            selector: row => row.EU==1 ? 'YES' : 'NO',
            cell: row => <YesNo on={row.EU==1}/>,
            sortable: true,
            width: '96px'
        },
        {
            name: 'VAT',
            selector: row => Number(row.VAT),
            cell: row => <span className="st-cell-mono">{row.VAT}</span>,
            sortable: true,
            width: '96px',
            right: true
        },
        {
            name: 'VAT FOR COMPANIES',
            selector: row => row.VATFLAG==1 ? 'YES' : 'NO',
            cell: row => <YesNo on={row.VATFLAG==1}/>,
            sortable: true,
            width: '190px'
        },
        {
            name: 'VAT FOR INDIVIDUALS',
            selector: row => row.VATFLAG2==1 ? 'YES' : 'NO',
            cell: row => <YesNo on={row.VATFLAG2==1}/>,
            sortable: true,
            width: '200px'
        },
        {
            name: 'COUNTRY CODE',
            selector: row => row.VIESCode,
            cell: row => <span className="st-cell-mono">{row.VIESCode}</span>,
            sortable: true,
            width: '150px'
        }
    ];

    useEffect(()=>{
        loadData()
    },[])

    /* Escape closes the dialog. */
    useEffect(()=>{
      if (!show) return undefined
      const onKey=(ev)=>{ if (ev.key==='Escape') handleClose() }
      document.addEventListener('keydown',onKey)
      return ()=>document.removeEventListener('keydown',onKey)
    },[show])

    const loadData=()=>{
        setIsLoading(true)
        setFailed(false)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/countries',{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                }
            })
          .then(res=>{
            setData(res.data)
            setIsLoading(false)
            loadLang()
          }).catch(err=>{
            setIsLoading(false)
            setFailed(true)
            setNote({tone:'crit',text:'The country list could not be loaded. Check your connection and try again.'})
            console.log(err)
          })
    }

    const loadLang=()=>{
        setIsLoading(true)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/lang',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
           setLang(res.data.results)
          setIsLoading(false)

        }).catch(err=>{
            setIsLoading(false)
            console.log(err)
        })
      }

    const showForm=(id)=>{
        setModalNote(null)
        setAskDelete(false)
        if (id==0){
            setModalTitle('New Country')
            setRecord({
                CountryID:0,
                EU:0,
                VATFLAG:0,
                VAT:0
            })

        }else{
            setRecord(data.filter(item=>item.CountryID==id)[0])
            setModalTitle(data.filter(item=>item.CountryID==id)[0].Country)
        }
        setShow(true)
    }

    const handleClose=()=>{
        setShow(false)
        setModalNote(null)
        setAskDelete(false)
    }

    const save=(e)=>{
        e.preventDefault()
        setModalNote(null)
        axios.post(process.env.REACT_APP_API_SERVER+'admin/countries',record,{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
          .then(res=>{
              if (res.data.err==0){
                loadData()
                setShow(false)
                setAskDelete(false)
                setNote({tone:'good',text:'Country saved.'})
              }else{
                  setModalNote({tone:'crit',text:'An error has occured, please try again'})
              }

          }).catch(err=>{
            setModalNote({tone:'crit',text:'An error has occured, please try again'})
          })

    }

    const deleteRecord=()=>{
          axios.delete(process.env.REACT_APP_API_SERVER+'admin/countries/'+record.CountryID,{
                headers: {
                    'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                  }
            })
            .then(res=>{
                if (res.data.err==0){
                    loadData()
                    setShow(false)
                    setAskDelete(false)
                    setNote({tone:'good',text:'Country deleted.'})
                }else{
                    setAskDelete(false)
                    setModalNote({tone:'crit',text:'An error has occured, please try again'})
                }

            }).catch(err=>{
                setAskDelete(false)
                setModalNote({tone:'crit',text:'An error has occured, please try again'})
                console.log(err)
            })
    }

    const langList=lang.map(item=>{
        return(
            <option key={item.LangID} value={item.LangID}>{item.LangName}</option>
        )
    })

    /* Every colour below comes from theme/tokens.css via getComputedStyle,
       so the table repaints with the rest of the product on a theme change. */
    const tableStyles=useMemo(()=>buildTableStyles(tokens),[tokens])

    const shown=useMemo(()=>{
      const needle=q.trim().toLowerCase()
      if (!needle) return data
      return data.filter(item=>{
        const name=String(item.Country||'').toLowerCase()
        const code=String(item.CountryCode||'').toLowerCase()
        const vies=String(item.VIESCode||'').toLowerCase()
        return name.indexOf(needle)>-1 || code.indexOf(needle)>-1 || vies.indexOf(needle)>-1
      })
    },[data,q])

    const total=data.length
    const field=(name)=>(record[name]===undefined || record[name]===null ? '' : record[name])

    return (
        <div className="st-page">

          {note
            ? <div className="st-note" data-tone={note.tone} role="alert">
                <span className="st-note-text">{note.text}</span>
                <button type="button" className="st-note-x" aria-label="Dismiss" onClick={()=>setNote(null)}>
                  <IconX/>
                </button>
              </div>
            : null}

          <Panel
            title="Χώρες"
            subtitle={
              isLoading
                ? 'Φόρτωση…'
                : total===0
                  ? 'Καμία εγγραφή'
                  : total+(total===1 ? ' χώρα' : ' χώρες')
            }
            actions={
              <>
                {total>0
                  ? <span className="st-search">
                      <IconSearch/>
                      <input
                        className="st-input"
                        type="search"
                        value={q}
                        placeholder="Αναζήτηση"
                        aria-label="Search countries"
                        onChange={(e)=>setQ(e.target.value)}
                      />
                    </span>
                  : null}
                <Btn variant="accent" onClick={()=>showForm(0)}>Προσθήκη Χώρας</Btn>
              </>
            }
          >
            {isLoading
              ? <div className="st-sk" aria-hidden="true">
                  <span className="skeleton"></span>
                  <span className="skeleton"></span>
                  <span className="skeleton"></span>
                  <span className="skeleton"></span>
                  <span className="skeleton"></span>
                </div>
              : failed
                ? <div className="st-empty">
                    <div className="st-empty-t">Οι χώρες δεν φορτώθηκαν</div>
                    <div className="st-empty-s">
                      Ο διακομιστής δεν απάντησε. Δεν χάθηκε τίποτα — δοκιμάστε ξανά.
                    </div>
                    <div className="st-empty-a">
                      <Btn variant="accent" onClick={loadData}>Δοκιμή ξανά</Btn>
                    </div>
                  </div>
                : total===0
                  ? <div className="st-empty">
                      <div className="st-empty-t">Δεν υπάρχουν χώρες</div>
                      <div className="st-empty-s">
                        Η χώρα ορίζει τη γλώσσα, τον κωδικό VIES και τον ΦΠΑ που εφαρμόζεται στις παραγγελίες.
                      </div>
                      <div className="st-empty-a">
                        <Btn variant="accent" onClick={()=>showForm(0)}>Προσθήκη Χώρας</Btn>
                      </div>
                    </div>
                  : shown.length===0
                    ? <div className="st-empty">
                        <div className="st-empty-t">Καμία αντιστοιχία για «{q.trim()}»</div>
                        <div className="st-empty-s">
                          Υπάρχουν {total} {total===1 ? 'χώρα' : 'χώρες'}. Καθαρίστε το φίλτρο για να τις δείτε όλες.
                        </div>
                        <div className="st-empty-a">
                          <Btn onClick={()=>setQ('')}>Καθαρισμός φίλτρου</Btn>
                        </div>
                      </div>
                    : <div className="st-table">
                        <DataTable
                          columns={columns}
                          data={shown}
                          customStyles={tableStyles}
                          highlightOnHover
                          persistTableHead
                          pagination
                          paginationPerPage={50}
                          paginationRowsPerPageOptions={[25,50,100,250]}
                          noDataComponent={<div className="st-empty-s">No countries to show.</div>}
                        />
                      </div>}
          </Panel>

          {show
            ? <div className="st-modal" role="dialog" aria-modal="true" aria-labelledby="st-country-title">
                <div className="st-modal-panel">

                  <div className="st-modal-head">
                    <div className="st-modal-headline">
                      <div className="st-modal-title" id="st-country-title">
                        <span>{modalTitle}</span>
                        {record.CountryID>0
                          ? <Flag className="st-flag" code={ record.CountryCode } height="22" width="22"/>
                          : null}
                      </div>
                      <div className="st-modal-sub">
                        {record.CountryID>0 ? 'Country settings' : 'Add a country'}
                      </div>
                    </div>
                    <button type="button" className="st-modal-x" aria-label="Close" onClick={handleClose}>
                      <IconX/>
                    </button>
                  </div>

                  <div className="st-modal-body">

                    {modalNote
                      ? <div className="st-note" data-tone={modalNote.tone} role="alert">
                          <span className="st-note-text">{modalNote.text}</span>
                        </div>
                      : null}

                    <form className="st-form" onSubmit={save}>

                      <div className="st-pair">
                        <div className="st-field">
                          <label className="mono st-label" htmlFor="st-country-name">Country Name</label>
                          <input
                            id="st-country-name"
                            className="st-input"
                            type="text"
                            required
                            value={field('Country')}
                            onChange={(e)=>setRecord({...record,Country:e.target.value})}
                          />
                        </div>

                        <div className="st-field">
                          <label className="mono st-label" htmlFor="st-country-code">Country Code</label>
                          <input
                            id="st-country-code"
                            className="st-input"
                            type="text"
                            required
                            value={field('CountryCode')}
                            onChange={(e)=>setRecord({...record,CountryCode:e.target.value})}
                          />
                          <div className="st-hint">Two-letter ISO code — it draws the flag.</div>
                        </div>
                      </div>

                      <div className="st-pair">
                        <div className="st-field">
                          <label className="mono st-label" htmlFor="st-country-lang">Language</label>
                          <select
                            id="st-country-lang"
                            className="st-select"
                            required
                            value={field('LangID')}
                            onChange={(e)=>setRecord({...record,LangID:e.target.value})}
                          >
                            <option value=''>[SELECT LANGUAGE]</option>
                            {langList}
                          </select>
                        </div>

                        <div className="st-field">
                          <label className="mono st-label" htmlFor="st-country-vies">VIES Code</label>
                          <input
                            id="st-country-vies"
                            className="st-input"
                            type="text"
                            required
                            value={field('VIESCode')}
                            onChange={(e)=>setRecord({...record,VIESCode:e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="st-group">
                        <div className="st-group-head">
                          <span className="mono st-group-title">VAT</span>
                        </div>

                        <div className="st-field">
                          <label className="mono st-label" htmlFor="st-country-vat">VAT Value %</label>
                          <input
                            id="st-country-vat"
                            className="st-input"
                            type="number"
                            min={0}
                            step={1}
                            required
                            value={field('VAT')}
                            onChange={(e)=>setRecord({...record,VAT:e.target.value})}
                          />
                        </div>

                        <div className="st-checks">
                          <label className="st-check">
                            <input
                              type="checkbox"
                              checked={record.EU==1}
                              onChange={(e)=>setRecord({...record,EU:e.target.checked ? 1 : 0})}
                            />
                            <span>
                              <span className="st-check-t">EU Country</span>
                            </span>
                          </label>

                          <label className="st-check">
                            <input
                              type="checkbox"
                              checked={record.VATFLAG==1}
                              onChange={(e)=>setRecord({...record,VATFLAG:e.target.checked ? 1 : 0})}
                            />
                            <span>
                              <span className="st-check-t">ADD VAT TO COMPANIES</span>
                            </span>
                          </label>

                          <label className="st-check">
                            <input
                              type="checkbox"
                              checked={record.VATFLAG2==1}
                              onChange={(e)=>setRecord({...record,VATFLAG2:e.target.checked ? 1 : 0})}
                            />
                            <span>
                              <span className="st-check-t">ADD VAT TO INDIVIDUALS</span>
                            </span>
                          </label>
                        </div>
                      </div>

                      {askDelete
                        ? <div className="st-confirm" role="alert">
                            <span className="st-confirm-t">
                              DELETE <b>{record.Country}</b>? This cannot be undone.
                            </span>
                            <span className="st-confirm-a">
                              <Btn onClick={()=>setAskDelete(false)}>CANCEL</Btn>
                              <Btn className="st-crit" onClick={deleteRecord}>DELETE</Btn>
                            </span>
                          </div>
                        : null}

                      <div className="st-foot">
                        {record.CountryID>0 && !askDelete
                          ? <span className="st-foot-far">
                              <Btn className="st-crit" onClick={()=>setAskDelete(true)}>DELETE</Btn>
                            </span>
                          : null}
                        <Btn onClick={handleClose}>CANCEL</Btn>
                        <Btn variant="accent" type="submit">SAVE</Btn>
                      </div>

                    </form>

                  </div>
                </div>
              </div>
            : null}

        </div>
    )
}
